<!-- The inline "New tag" field: with several projects to choose from (the Hooks and Sources bars with "All projects"),
     "in project" and one pill per project (ProjectPills.vue); then the name field (30 characters), ✓ and ×. Enter or ✓ creates the tag in
     the chosen project (addTag: unless it exists already, without case) and emits done with the tag as stored (null for
     an empty name); Esc or × emits cancel. It takes the cursor when it appears. Used by the Hooks bar, the Sources "Add
     tag" tile and the tag picker of the dialogs (TagPicker.vue, which calls confirm() through a template ref to keep a
     tag typed but not confirmed), and, as a full-width row (block), by the tag lists of "Manage tags" and Settings
     (TagList.vue). The owner shows and hides it (TAGSTATE[kind].adding, src/stores/tags.ts). -->
<script setup lang="ts">
import { onMounted, ref } from 'vue'
import ProjectPills from './ProjectPills.vue'
import { useI18n } from '../../composables/useI18n'
import { useDataStore } from '../../stores/data'
import { addTag } from '../../features/tags'
import { saveAppData } from '../../features/storage/save'
import type { Project, TagGroup } from '../../data/types'

const props = withDefaults(defineProps<{ kind: TagGroup; inputId?: string; pid: string; projects?: Project[]; block?: boolean }>(), { inputId: undefined, projects: () => [], block: false })
const emit = defineEmits<{ pick: [pid: string]; done: [tag: string | null]; cancel: [] }>()
const { t } = useI18n()

const name = ref('')
const input = ref<HTMLInputElement | null>(null)
onMounted(() => input.value?.focus())
function pick(pid: string) { emit('pick', pid); input.value?.focus() }
/** Creates the typed tag in the project; returns it as stored (null: empty name) */
function confirm(): string | null {
  const d = useDataStore().getData(), r = addTag(d, props.kind, props.pid, name.value)
  name.value = ''
  if (r?.created) saveAppData(d, true)
  emit('done', r ? r.tag : null)
  return r ? r.tag : null
}
defineExpose({ confirm })
</script>

<template>
  <span :class="block ? 'flex items-center gap-2 p-2 rounded-xl border border-brand bg-brand/10' : 'inline-flex flex-wrap items-center gap-1'">
    <ProjectPills :projects="projects" :pid="pid" @pick="pick" />
    <input :id="inputId" ref="input" v-model="name" maxlength="30" :placeholder="t('New tag')"
      :class="block ? 'flex-1 min-w-0 bg-darkinput border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-100 focus:outline-none focus:border-brand' : 'w-32 bg-darkinput border border-brand rounded-full px-3 py-1.5 text-xs text-gray-100 focus:outline-none'"
      @keydown.enter.prevent="confirm" @keydown.esc.stop="emit('cancel')">
    <button type="button" :title="t('Create tag')" :class="[block ? 'w-8 h-8 flex-shrink-0' : 'w-7 h-7', 'rounded-full bg-brand text-white text-xs']" @click="confirm"><i class="fas fa-check"></i></button>
    <button type="button" :title="t('Cancel')" :class="[block ? 'w-8 h-8 flex-shrink-0' : 'w-7 h-7', 'rounded-full bg-darkbtn text-gray-400 text-xs']" @click="emit('cancel')"><i class="fas fa-xmark"></i></button>
  </span>
</template>
